import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/Select';
import { cn } from '@shared/components/ui/cn';
import { SUPPORTED_LANGS } from '@shared/lib/i18n/config';

/** Display labels for each supported language (endonyms). */
const LANG_LABELS: Record<(typeof SUPPORTED_LANGS)[number], string> = {
  en: 'English',
  ru: 'Русский',
  kk: 'Қазақша',
  ky: 'Кыргызча',
  ms: 'Bahasa Melayu',
  id: 'Bahasa Indonesia',
  fil: 'Filipino',
  hi: 'हिन्दी',
  zh: '中文',
  ar: 'العربية',
};

/**
 * Language selector for the auth screens (DEV-3110). Changing the value calls
 * `i18n.changeLanguage`, which i18next persists to the `i18next` cookie, so the
 * choice survives navigation and reload.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation('auth');

  // i18n.language can be a region variant ("en-US"); match on the base tag.
  const current =
    SUPPORTED_LANGS.find((lng) => i18n.language === lng || i18n.language.startsWith(`${lng}-`)) ??
    'en';

  return (
    <Select value={current} onValueChange={(lng) => void i18n.changeLanguage(lng)}>
      <SelectTrigger
        className={cn('h-9 w-auto gap-2 px-3 text-sm font-normal', className)}
        aria-label={t('language', 'Language')}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LANGS.map((lng) => (
          <SelectItem key={lng} value={lng}>
            {LANG_LABELS[lng]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
